import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Trash2, Pencil, Check, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const ETIQ = {
  acuerdo: ["Acuerdo", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  visita: ["Visita del delegado", "bg-sky-50 text-sky-700 border-sky-200"],
  opinion: ["Opinión", "bg-violet-50 text-violet-700 border-violet-200"],
  pendiente: ["Pendiente", "bg-amber-50 text-amber-700 border-amber-200"],
};
const hoy = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const fechaES = (f) => new Date(`${f}T00:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

function Editor({ inicial, onGuardar, onCancelar, guardando, testid }) {
  const [n, setN] = useState(inicial);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-2">
        {Object.entries(ETIQ).map(([k, [l, cls]]) => (
          <button key={k} type="button" onClick={() => setN({ ...n, etiqueta: k })} data-testid={`${testid}-etiq-${k}`}
                  className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${n.etiqueta === k ? cls + " ring-2 ring-offset-1 ring-slate-300" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
        <input type="date" value={n.fecha} max={hoy()} onChange={(e) => setN({ ...n, fecha: e.target.value || hoy() })} className="input ml-auto" style={{ width: 160 }} data-testid={`${testid}-fecha`} />
      </div>
      <textarea value={n.texto} onChange={(e) => setN({ ...n, texto: e.target.value })} rows={3} maxLength={5000} data-testid={`${testid}-texto`}
                placeholder="Qué te parece el laboratorio, qué has quedado con el delegado, condiciones, próximos pasos…"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200" />
      <div className="flex gap-2 justify-end mt-2">
        {onCancelar && <button onClick={onCancelar} className="btn btn-outline btn-sm" data-testid={`${testid}-cancelar`}><X size={14} /> Cancelar</button>}
        <button onClick={() => onGuardar(n)} disabled={!n.texto.trim() || guardando} className="btn btn-primary btn-sm" data-testid={`${testid}-guardar`}>
          {guardando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar nota</button>
      </div>
    </div>
  );
}

function Nota({ n, onCambio, onBorrar }) {
  const [editando, setEditando] = useState(false);
  const [l, cls] = ETIQ[n.etiqueta] || ETIQ.opinion;
  if (editando) return <Editor inicial={n} testid={`nota-editor-${n.id}`} onCancelar={() => setEditando(false)} onGuardar={async (x) => { await onCambio(n.id, { texto: x.texto, etiqueta: x.etiqueta, fecha: x.fecha }); setEditando(false); }} />;
  return (
    <li className="relative pl-6 pb-5 border-l-2 border-slate-200 last:pb-0" data-testid={`nota-${n.id}`}>
      <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-2 border-sky-500" />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold text-slate-700">{fechaES(n.fecha)}</span>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{l}</span>
        {n.etiqueta === "pendiente" && (
          <label className="text-xs text-slate-600 inline-flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={!!n.hecho} onChange={(e) => onCambio(n.id, { hecho: e.target.checked })} data-testid={`nota-hecho-${n.id}`} /> Hecho
          </label>
        )}
        <span className="ml-auto flex gap-1">
          <button onClick={() => setEditando(true)} aria-label="Editar" className="p-1.5 rounded text-slate-400 hover:text-sky-700 hover:bg-sky-50" data-testid={`nota-editar-${n.id}`}><Pencil size={14} /></button>
          <button onClick={() => onBorrar(n.id)} aria-label="Borrar" className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`nota-borrar-${n.id}`}><Trash2 size={14} /></button>
        </span>
      </div>
      <p className={`text-sm mt-1 whitespace-pre-wrap ${n.hecho ? "line-through text-slate-400" : "text-slate-700"}`}>{n.texto}</p>
    </li>
  );
}

export function NotasLaboratorio({ lab }) {
  const [notas, setNotas] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [guardando, setGuardando] = useState(false);
  const [k, setK] = useState(0);

  useEffect(() => { setNotas(null); api.get("/lab-notas", { params: { laboratorio: lab } }).then((r) => setNotas(r.data.notas)).catch(() => setNotas([])); }, [lab]);
  const ordenar = (xs) => [...xs].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.created_at.localeCompare(a.created_at));
  const err = (e, m) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || m);

  const crear = async (n) => {
    setGuardando(true);
    try { const { data } = await api.post("/lab-notas", { laboratorio: lab, ...n }); setNotas((x) => ordenar([data, ...x])); setK((v) => v + 1); toast.success("Nota guardada"); }
    catch (e) { err(e, "No se pudo guardar la nota"); } finally { setGuardando(false); }
  };
  const cambiar = async (id, upd) => {
    try { const { data } = await api.patch(`/lab-notas/${id}`, upd); setNotas((x) => ordenar(x.map((n) => (n.id === id ? data : n)))); }
    catch (e) { err(e, "No se pudo actualizar la nota"); }
  };
  const borrar = async (id) => {
    if (!window.confirm("¿Borrar esta nota?")) return;
    try { await api.delete(`/lab-notas/${id}`); setNotas((x) => x.filter((n) => n.id !== id)); }
    catch (e) { err(e, "No se pudo borrar la nota"); }
  };

  const visibles = (notas || []).filter((n) => filtro === "todas" || n.etiqueta === filtro);
  const pendientes = (notas || []).filter((n) => n.etiqueta === "pendiente" && !n.hecho).length;
  return (
    <div className="card p-6" data-testid="lab-notas">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><NotebookPen size={18} className="text-sky-700" /> Diario de {lab}</h3>
          <p className="text-xs text-slate-500">Apunta lo que te parece el laboratorio y lo que acuerdas con el delegado. Solo lo ves tú.</p>
        </div>
        {pendientes > 0 && <span className="text-xs font-semibold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="lab-notas-pendientes">{pendientes} {pendientes === 1 ? "pendiente" : "pendientes"}</span>}
      </div>
      <Editor key={k} inicial={{ texto: "", etiqueta: "acuerdo", fecha: hoy() }} onGuardar={crear} guardando={guardando} testid="nota-nueva" />
      {notas === null ? <div className="text-sm text-slate-500 mt-4 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando notas…</div> : notas.length === 0 ? (
        <p className="text-sm text-slate-500 mt-4" data-testid="lab-notas-vacio">Aún no hay notas de este laboratorio.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5 mt-5 mb-4" data-testid="lab-notas-filtros">
            {[["todas", "Todas"], ...Object.entries(ETIQ).map(([k2, [l]]) => [k2, l])].map(([k2, l]) => (
              <button key={k2} onClick={() => setFiltro(k2)} data-testid={`lab-notas-filtro-${k2}`}
                      className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${filtro === k2 ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
            ))}
          </div>
          <ul className="ml-1.5" data-testid="lab-notas-lista">
            {visibles.map((n) => <Nota key={n.id} n={n} onCambio={cambiar} onBorrar={borrar} />)}
            {!visibles.length && <li className="text-sm text-slate-500">No hay notas con esta etiqueta.</li>}
          </ul>
        </>
      )}
    </div>
  );
}
